
import ziperase from '../../services/ziperase';
import { notify } from '../../services/notify';
import auth from '../../services/auth';
import config from '../../constants/config';
import errorHandler from '../../services/errorHandler';

const hasRole = (user, role) => {
  return user ? user.role.id === role : false;
};

const state = {
  user: null,
  selectedUser: null,
  selectedMachineUser: null,
  roles: null,
  accountManagers: null,
  apiKey: null,
}

const mutations = {
  user (state, user) {
    state.user = user;
  },
  userHasApiKey(state, b) {
    state.user.has_api_key = b;
  },
  selectUser(state, user) {
    state.selectedUser = user;
  },
  selectMachineUser(state, user) {
    state.selectedMachineUser = user;
  },
  roles(state, roles) {
    state.roles = roles;
  },
  accountManagers(state, accountManagers) {
    state.accountManagers = accountManagers;
  },
  apiKey(state, apiKey) {
    state.apiKey = apiKey;
  }
}

const getters = {
  user: () => state.user,
  selectedUser: () => state.selectedUser,
  selectedMachineUser: () => state.selectedMachineUser,
  roles: () => state.roles,
  isSuperUser: () => hasRole(state.user, config.SUPERUSER),
  isStaffUser: () => hasRole(state.user, config.STAFF_USER),
  isPartner: () => hasRole(state.user, config.PARTNER),
  isManager: () => hasRole(state.user, config.MANAGER),
  isMember: () => hasRole(state.user, config.MEMBER),
  accountManagers: () => state.accountManagers,
  apiKey: () => state.apiKey,
}

const actions = {
  async login({}, { email, password }) {
    try {
      const response = await ziperase.login(email, password);
      auth.setAccessToken(response.data.access_token);
      return true;
    } catch (error) {
      notify().error('Failed to login.');
    }
    return false;
  },
  async fetchMe({ commit, getters }) {
    try {
      const response = await ziperase.fetchMe();
      commit('user', response.data);

      if (!getters.isSuperUser && !getters.isStaffUser && !getters.isPartner) {
        commit('organization/selectOrganization', getters.user.organization, { root: true });
        commit('group/topLevelGroup', getters.user.group, { root: true });
      }
    } catch (error) {
      notify().error('Failed to retrieve user information.');
    }
  },
  async updateMe({ commit }, user) {
    try {
      const response = await ziperase.updateMe(user);
      commit('user', response.data);
      notify().success('Success! Your account has been updated.');
      return true;
    } catch (error) {
      notify().error('Failed to retrieve user information.');
    }
    return false;
  },
  async createApiKey({ commit }) {
    try {
      const response = await ziperase.createApiKey();
      commit('apiKey', response.data.key);
      commit('userHasApiKey', true);
      notify().success('Success! Your API key was reset.');
      return true;
    } catch (error) {
      notify().error('Failed to reset API key.');
    }
    return false;
  },
  async deleteApiKey({ commit }) {
    try {
      const response = await ziperase.deleteApiKey();
      commit('apiKey', null);
      commit('userHasApiKey', false);
      notify().success('Success! Your API key was removed.');
      return true;
    } catch (error) {
      notify().error('Failed to remove API key.');
    }
    return false;
  },
  async clearApiKey({ commit }) {
    commit('apiKey', null);
  },
  async resendConfirmUserEmail({ commit }, user) {
    try {
      const response = await ziperase.resendConfirmUserEmail(user.id);
      notify().success('Success! Confirmation email sent.');
      return true;
    } catch (error) {
      notify().error('Failed to send confirmation email for ' + user.email);
    }
    return false;
  },
  async fetchRoles({ commit }) {
    try {
      const response = await ziperase.fetchRoles();
      commit('roles', response.data);
    } catch (error) {
      notify().error('Failed to retrieve user roles.');
    }
  },
  async fetchAccountManagers({ commit }) {
    try {
      const response = await ziperase.fetchAccountManagers();
      commit('accountManagers', response.data);
    } catch (error) {
      notify().error('Failed to retrieve account managers.');
    }
  },
  async createUser({}, user) {
    try {
      await ziperase.createUser(user);
      return true;
    } catch (error) {
      let errorMessage = null;
      if (errorHandler.isConflictError(error)) {
        errorMessage = errorHandler.getConflictErrorMessage(error);
      }
      notify().error(errorMessage || `Failed to create ${user.full_name}.`);
    }
    return false;
  },
  async updateUser({ commit }, user) {
    try {
      const response = await ziperase.updateUser(user.id, user);
      commit('selectUser', response.data);
      notify().success(`Success! Updated ${response.data.full_name}`);
      return true;
    } catch (error) {
      let errorMessage = null;
      if (errorHandler.isConflictError(error)) {
        errorMessage = errorHandler.getConflictErrorMessage(error);
      }
      notify().error(errorMessage || `Failed to update ${user.full_name}.`);
    }
    return false;
  },
  async toggleActivation({ commit }, user) {
    try {
      let response = null;
      let update = "";
      if (user.is_active) {
        response = await ziperase.deactivateUser(user.id);
        update = "deactivated";
      } else {
        response = await ziperase.activateUser(user.id);
        update = "activated";
      }
      commit('selectUser', response.data);
      notify().success(`Success! ${user.full_name} has been ${update}.`);
      return true;
    } catch (error) {
      notify().error('Failed to update user.');
    }
    return false;
  },
  async toggleActivationMachineUser({ commit }, user) {
    try {
      let response = null;
      let update = "";
      if (user.is_active) {
        response = await ziperase.deactivateMachineUser(user.id);
        update = "deactivated";
      } else {
        response = await ziperase.activateMachineUser(user.id);
        update = "activated";
      }
      commit('selectMachineUser', response.data);
      notify().success(`Success! ${user.description} has been ${update}.`);
      return true;
    } catch (error) {
      notify().error('Failed to update user.');
    }
    return false;
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
